import {
  Grid2,
  Typography,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Link } from '@liminal/web-ui/primitives/Link';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { DetailRow } from '@liminal/web-ui/display/DetailRow';
import { EmptyState } from '@liminal/web-ui/feedback/EmptyState';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { styled } from '@mui/material/styles';
import { useUserDetail, useUserKycStatus, useUserKycData, useUserPreferences, useUserDevices } from '@/hooks/useUsers';
import { formatOrderDate } from '@liminal/utils/date';
import { StatusPill, type StatusPillTone } from '@liminal/web-ui/primitives/StatusPill';
import {
  kycStatusVariant,
  kycStatusLabel,
  formatEnumLabel,
  acquisitionSourceLabel,
  onboardingGoalLabel,
  personaStatusLabel,
  personaStatusVariant,
  providerLabel,
} from '@/components/ui/statusHelpers';
import { UserIdLink } from '@/components/ui/UserIdLink';
import { OnboardingChatCard } from './OnboardingChatCard';
import type { ProviderStatus, PersonaProviderDetails } from '@/api/types';
import type { InquiryState } from '@/api/generated';
import { RampProvider } from '@/api/generated';
import { fontWeights } from '@liminal/brand/scale';
import { ADMIN_ENV, type AdminEnv } from '@/lib/env';

// -- Styled components --

// NOT one of the no-op wrappers its sibling sections carried: fontWeight 600
// duplicates what theme.ts already gives variant="h6", but the bottom margin is
// real and six headings in this file depend on it. Only the redundant half goes.
const SectionHeading = styled(Typography)({
  marginBottom: 16,
});

const SubsectionHeading = styled(Typography)({
  marginTop: 8,
});

const TypePreferencesHeading = styled(Typography)({
  marginTop: 8,
  marginBottom: 8,
});

const KycStatusColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const AvatarColorSwatch = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ColorDot = styled(Box)(({ theme }) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  border: '1px solid',
  borderColor: theme.palette.divider,
}));

// -- Helpers --

// SourceCardShell renders the common chrome for any verification source card:
// header row with the source name + status pill, then a vertical stack of children.
function SourceCardShell({
  name,
  statusLabel,
  statusVariant,
  children,
}: {
  name: string;
  statusLabel: string;
  statusVariant: StatusPillTone;
  children?: React.ReactNode;
}) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: fontWeights.semibold }}>{name}</Typography>
          <StatusPill label={statusLabel} tone={statusVariant} />
        </Box>
        <Stack spacing={1.25}>{children}</Stack>
    </Card>
  );
}

function PersonaSourceCard({
  personaStatus,
  personaDetails,
}: {
  personaStatus?: InquiryState;
  personaDetails?: PersonaProviderDetails;
}) {
  return (
    <SourceCardShell
      name="Persona"
      statusLabel={personaStatusLabel(personaStatus)}
      statusVariant={personaStatusVariant(personaStatus)}
    >
      <DetailRow
        layout="stacked"
        label="Inquiry ID"
        value={
          personaDetails?.inquiryId ? (
            <Link
              href={`https://app.withpersona.com/dashboard/inquiries/${personaDetails.inquiryId}`}
              external
            >
              {personaDetails.inquiryId}
            </Link>
          ) : (
            '\u2014'
          )
        }
      />
      <DetailRow
        layout="stacked"
        label="Decision"
        value={personaDetails?.decision ? formatEnumLabel(personaDetails.decision) : '\u2014'}
      />
      <DetailRow layout="stacked" label="Decision Reason" value={personaDetails?.decisionReason ?? '\u2014'} />
    </SourceCardShell>
  );
}

// BlindPay's dashboard nests every customer under the instance, which differs per
// environment. dev has none: its blindpay-credentials hold a placeholder
// (env/liminal/secrets/), so dev shows no link rather than a wrong one. testnet
// runs on BlindPay's development instance, whose id is not copied here yet, so
// it shows no link either.
const BLINDPAY_INSTANCE_IDS: Record<AdminEnv, string | undefined> = {
  dev: undefined,
  testnet: undefined,
  prod: 'in_EJ50Xo13ChDm',
};

// Bridge and BlindPay never return a verification link from their APIs, unlike
// Fern/Avenia/Rain (core/fincrime/kyc/kyc_service/service/service.go). Their dashboard
// URLs are built from the customer id, the same way PersonaSourceCard builds its inquiry link.
function providerDashboardLink(provider: ProviderStatus): string | undefined {
  const customerId = provider.providerCustomerId;
  if (!customerId) {
    return undefined;
  }
  if (provider.provider === RampProvider.PROVIDER_BRIDGE) {
    return `https://dashboard.bridge.xyz/app/customers/${customerId}`;
  }
  const blindpayInstanceId = BLINDPAY_INSTANCE_IDS[ADMIN_ENV];
  if (provider.provider === RampProvider.PROVIDER_BLINDPAY && blindpayInstanceId) {
    return `https://app.blindpay.com/instances/${blindpayInstanceId}/customers/${customerId}/profile`;
  }
  return undefined;
}

function ProviderSourceCard({ provider }: { provider: ProviderStatus }) {
  const issues = provider.verificationIssues ?? [];
  const paymentMethods = provider.availablePaymentMethods ?? [];
  // Bridge sends verificationLink as "" rather than omitting it, so `??` never
  // falls through to the constructed dashboard link — `||` treats "" as absent too.
  const verificationLink = provider.verificationLink || providerDashboardLink(provider);
  return (
    <SourceCardShell
      name={providerLabel(provider.provider)}
      statusLabel={kycStatusLabel(provider.status)}
      statusVariant={kycStatusVariant(provider.status)}
    >
      <DetailRow layout="stacked" label="Customer ID" value={provider.providerCustomerId ?? '\u2014'} />
      <DetailRow layout="stacked" label="Raw Status" value={provider.rawStatus ?? '\u2014'} />
      <DetailRow
        layout="stacked"
        label="Verification Link"
        value={
          verificationLink ? (
            <Link href={verificationLink} external>
              Open
            </Link>
          ) : (
            '\u2014'
          )
        }
      />
      {paymentMethods.length > 0 && (
        <DetailRow
          layout="stacked"
          label="Payment Methods"
          value={
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {paymentMethods.map((m) => (
                <StatusPill key={m} label={m} tone="neutral" size="sm" />
              ))}
            </Box>
          }
        />
      )}
      {issues.length > 0 && (
        <DetailRow
          layout="stacked"
          label="Issues"
          value={
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {issues.map((issue, idx) => (
                <TableRow key={`${issue.code ?? 'issue'}-${idx}`}>
                  <TableCell>{issue.code ?? '\u2014'}</TableCell>
                  <TableCell>{issue.message ?? '\u2014'}</TableCell>
                  <TableCell>{issue.timestamp ?? '\u2014'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          }
        />
      )}
    </SourceCardShell>
  );
}

export function ProfileKycSection({ userId }: { userId: string }) {
  const { data: user, isLoading: userLoading, isError: userError } = useUserDetail(userId);
  const { data: kycStatus, isLoading: kycStatusLoading, isError: kycStatusError } = useUserKycStatus(userId);
  const { data: kycData, isLoading: kycDataLoading, isError: kycDataError } = useUserKycData(userId);
  const { data: preferences, isLoading: prefsLoading, isError: prefsError } = useUserPreferences(userId);
  const { data: devicesData, isError: devicesError } = useUserDevices(userId);

  // A failed request says which one failed, above the cards that still
  // rendered. It used to read as the empty copy of whichever card it fed.
  const failed = [
    userError && 'profile',
    kycStatusError && 'KYC status',
    kycDataError && 'verified identity',
    prefsError && 'notification preferences',
    devicesError && 'devices',
  ].filter((name): name is string => typeof name === 'string');

  // Most recently seen active device (for the Device Info card)
  const devices = devicesData?.devices ?? [];
  const activeDevice = devices
    .filter((d) => d.isActive)
    .sort((a, b) => {
      const aTime = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0;
      const bTime = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0;
      return bTime - aTime;
    })[0];

  const isLoading = userLoading || kycStatusLoading || kycDataLoading || prefsLoading;

  if (isLoading) {
    return (
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <Card variant="elevated"><Skeleton height={300} /></Card>
        </Grid2>
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <Card variant="elevated"><Skeleton height={300} /></Card>
        </Grid2>
      </Grid2>
    );
  }

  const profileItems: { label: string; value: React.ReactNode }[] = user ? [
    { label: 'User ID', value: <UserIdLink userId={user.userId} full /> },
    { label: 'Email', value: user.email },
    { label: 'First Name', value: user.firstName },
    { label: 'Last Name', value: user.lastName },
    { label: 'Display Tag', value: user.displayTag ? `@${user.displayTag}` : '\u2014' },
    { label: 'Phone Number', value: user.phoneNumber },
    { label: 'Phone Verified', value: (
      <StatusPill
        label={user.phoneNumberVerified ? 'Yes' : 'No'}
        tone={user.phoneNumberVerified ? 'success' : 'neutral'}
        size="sm"
      />
    ) },
    { label: 'Previous Display Tag', value: user.previousDisplayTag ? `@${user.previousDisplayTag}` : '\u2014' },
    { label: 'Nationality', value: user.nationality || '\u2014' },
    { label: 'Tier', value: user.tier || '\u2014' },
    { label: 'Time Zone', value: user.timeZone || '\u2014' },
    { label: 'Goal', value: onboardingGoalLabel(user.onboardingGoal) },
    { label: 'Heard About Us', value: acquisitionSourceLabel(user.acquisitionSource, user.acquisitionSourceDetail) },
    { label: 'Avatar Color', value: user.avatarColor ? (
      <AvatarColorSwatch>
        <ColorDot sx={{ bgcolor: user.avatarColor }} />
        <Typography variant="body2">{user.avatarColor}</Typography>
      </AvatarColorSwatch>
    ) : '\u2014' },
    { label: 'Onboarding Completed', value: (
      <StatusPill
        label={user.isOnboardingCompleted ? 'Yes' : 'No'}
        tone={user.isOnboardingCompleted ? 'success' : 'neutral'}
        size="sm"
      />
    ) },
    { label: 'Created At', value: user.createdAt ? formatOrderDate(user.createdAt) : '\u2014' },
    { label: 'Updated At', value: user.updatedAt ? formatOrderDate(user.updatedAt) : '\u2014' },
  ] : [];

  return (
    <Stack spacing={3}>
      {failed.length > 0 && (
        <WarningCard tone="error">Could not load this user's {failed.join(', ')}.</WarningCard>
      )}

      <Grid2 container spacing={3}>
        {/* Profile Card */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <Card variant="elevated">
              <SectionHeading variant="h6">
                Profile
              </SectionHeading>
              <Grid2 container spacing={2}>
                {profileItems.map((item) => (
                  <Grid2 size={{ xs: 12, sm: 6 }} key={item.label}>
                    <DetailRow layout="stacked" label={item.label} value={item.value ?? '\u2014'} />
                  </Grid2>
                ))}
              </Grid2>
          </Card>
        </Grid2>

        {/* KYC Status Card */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <Card variant="elevated">
              <SectionHeading variant="h6">
                KYC Status
              </SectionHeading>

              <KycStatusColumn>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Provider</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Persona</TableCell>
                      <TableCell>
                        <StatusPill
                          label={personaStatusLabel(kycStatus?.personaStatus)}
                          tone={personaStatusVariant(kycStatus?.personaStatus)}
                          size="sm"
                        />
                      </TableCell>
                    </TableRow>
                    {(kycStatus?.providers ?? []).map((p) => (
                      <TableRow key={p.provider ?? 'unknown'}>
                        <TableCell>{providerLabel(p.provider)}</TableCell>
                        <TableCell>
                          <StatusPill
                            label={kycStatusLabel(p.status)}
                            tone={kycStatusVariant(p.status)}
                            size="sm"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <DetailRow layout="stacked" label="Overall Status" value={<StatusPill
                          label={kycStatusLabel(kycStatus?.overallStatus)}
                          tone={kycStatusVariant(kycStatus?.overallStatus)}
                          size="sm"
                        />} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <DetailRow layout="stacked" label="Is Verified" value={<StatusPill
                          label={kycStatus?.isVerified ? 'Yes' : 'No'}
                          tone={kycStatus?.isVerified ? 'success' : 'neutral'}
                          size="sm"
                        />} />
                  </Grid2>
                </Grid2>
              </KycStatusColumn>
          </Card>
        </Grid2>

        {/* Device Info Card */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <Card variant="elevated">
              <SectionHeading variant="h6">
                Device Info
              </SectionHeading>
              {activeDevice ? (
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <DetailRow layout="stacked" label="Platform" value={<StatusPill
                          label={activeDevice.platform?.includes('IOS') ? 'iOS' : activeDevice.platform?.includes('ANDROID') ? 'Android' : 'Web'}
                          tone={activeDevice.platform?.includes('IOS') ? 'info' : activeDevice.platform?.includes('ANDROID') ? 'success' : 'warning'}
                          size="sm"
                        />} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <DetailRow layout="stacked" label="App Version" value={activeDevice.appVersion ?? '\u2014'} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <DetailRow layout="stacked" label="OS Version" value={activeDevice.osVersion ?? '\u2014'} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <DetailRow layout="stacked" label="Last Seen" value={(activeDevice.lastSeenAt ? formatOrderDate(activeDevice.lastSeenAt) : undefined) ?? '\u2014'} />
                  </Grid2>
                </Grid2>
              ) : (
                <Typography variant="body2" color="text.disabled">No active devices.</Typography>
              )}
          </Card>
        </Grid2>

        {/* Onboarding Chat Card */}
        <Grid2 size={{ xs: 12, lg: 6 }}>
          <OnboardingChatCard userId={userId} />
        </Grid2>
      </Grid2>

      {/* Verification Sources — one card per source (Persona + each ramp provider).
          Scales horizontally as more providers are added: 1 col on mobile, 2 col
          on tablet, 3 col on desktop. */}
      <Card variant="elevated">
          <SectionHeading variant="h6">
            Verification Sources
          </SectionHeading>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
              <PersonaSourceCard
                personaStatus={kycStatus?.personaStatus}
                personaDetails={kycStatus?.personaDetails}
              />
            </Grid2>
            {kycStatus?.providers?.map((p) => (
              <Grid2 key={p.provider ?? 'unknown'} size={{ xs: 12, md: 6, lg: 4 }}>
                <ProviderSourceCard provider={p} />
              </Grid2>
            ))}
            {(!kycStatus?.providers || kycStatus.providers.length === 0) && (
              <Grid2 size={{ xs: 12 }}>
                <Typography variant="body2" color="text.disabled">
                  No ramp provider verifications yet.
                </Typography>
              </Grid2>
            )}
          </Grid2>
      </Card>

      {/* Verified Identity Card */}
      <Card variant="elevated">
          <SectionHeading variant="h6">
            Verified Identity
          </SectionHeading>
          {kycData ? (
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="User ID" value={<UserIdLink userId={kycData.userId} full />} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="First Name" value={kycData.firstName ?? '\u2014'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="Middle Name" value={kycData.middleName ?? '\u2014'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="Last Name" value={kycData.lastName ?? '\u2014'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="Date of Birth" value={kycData.birthdate ?? '\u2014'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="ID Class" value={formatEnumLabel(kycData.idClass)} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="ID Issuing Country" value={kycData.idIssuingCountry ?? '\u2014'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="ID Expiration Date" value={kycData.idExpirationDate ?? '\u2014'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="Verified At" value={kycData.verifiedAt ? formatOrderDate(kycData.verifiedAt) : '\u2014'} />
              </Grid2>
              {kycData.address && (
                <>
                  <Grid2 size={{ xs: 12 }}>
                    <SubsectionHeading variant="subtitle2">Address</SubsectionHeading>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <DetailRow layout="stacked" label="Street 1" value={kycData.address.street1 ?? '\u2014'} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <DetailRow layout="stacked" label="Street 2" value={kycData.address.street2 ?? '\u2014'} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <DetailRow layout="stacked" label="City" value={kycData.address.city ?? '\u2014'} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <DetailRow layout="stacked" label="State / Province" value={kycData.address.subdivision ?? '\u2014'} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <DetailRow layout="stacked" label="Postal Code" value={kycData.address.postalCode ?? '\u2014'} />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <DetailRow layout="stacked" label="Country" value={kycData.address.countryCode ?? '\u2014'} />
                  </Grid2>
                </>
              )}
            </Grid2>
          ) : (
            <EmptyState icon="person" size="sm" title="No verified identity data" />
          )}
      </Card>

      {/* Notification Preferences Card */}
      <Card variant="elevated">
          <SectionHeading variant="h6">
            Notification Preferences
          </SectionHeading>
          {preferences ? (
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="User ID" value={<UserIdLink userId={preferences.userId} full />} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="Email Enabled" value={<StatusPill label={preferences.emailEnabled ? 'Yes' : 'No'} tone={preferences.emailEnabled ? 'success' : 'neutral'} size="sm" />} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="Web Push Enabled" value={<StatusPill label={preferences.webPushEnabled ? 'Yes' : 'No'} tone={preferences.webPushEnabled ? 'success' : 'neutral'} size="sm" />} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="Mobile Push Enabled" value={<StatusPill label={preferences.mobilePushEnabled ? 'Yes' : 'No'} tone={preferences.mobilePushEnabled ? 'success' : 'neutral'} size="sm" />} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="SMS Enabled" value={<StatusPill label={preferences.smsEnabled ? 'Yes' : 'No'} tone={preferences.smsEnabled ? 'success' : 'neutral'} size="sm" />} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="WhatsApp Enabled" value={<StatusPill label={preferences.whatsappEnabled ? 'Yes' : 'No'} tone={preferences.whatsappEnabled ? 'success' : 'neutral'} size="sm" />} />
              </Grid2>
              {preferences.typePreferences && Object.keys(preferences.typePreferences).length > 0 && (
                <Grid2 size={{ xs: 12 }}>
                  <TypePreferencesHeading variant="subtitle2">Type Preferences</TypePreferencesHeading>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Notification Type</TableCell>
                        <TableCell>Enabled</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(preferences.typePreferences).map(([key, enabled]) => (
                        <TableRow key={key}>
                          <TableCell>{key}</TableCell>
                          <TableCell>
                            <StatusPill label={enabled ? 'Yes' : 'No'} tone={enabled ? 'success' : 'neutral'} size="sm" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid2>
              )}
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="Created At" value={preferences.createdAt ? formatOrderDate(preferences.createdAt) : '\u2014'} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <DetailRow layout="stacked" label="Updated At" value={preferences.updatedAt ? formatOrderDate(preferences.updatedAt) : '\u2014'} />
              </Grid2>
            </Grid2>
          ) : (
            <EmptyState icon="notifications" size="sm" title="No notification preferences" />
          )}
      </Card>
    </Stack>
  );
}
